import React, { useEffect, useCallback, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'motion/react';
import {
  ExternalLink,
  AlertCircle,
  Clock3,
  Package,
  Lock,
  Ban,
  Monitor,
  Smartphone,
  Tablet,
  CheckCircle2,
  LayoutGrid,
} from 'lucide-react';
import { usePortal } from '@/store/portal';
import type { PortalApp } from '@/store/portal';
import { Loader } from '@/components/loader';
import { http } from '@/libs/http';
import { toast } from 'sonner';

export type SupportedPlatform = 'web' | 'android' | 'ios';
export type FilterTab = 'all' | 'web' | 'android' | 'ios';

export function detectPlatform(): SupportedPlatform {
  if (typeof window === 'undefined') return 'web';
  const ua = navigator.userAgent || navigator.vendor || (window as any).opera || '';
  if (/android/i.test(ua)) {
    return 'android';
  }
  if (/iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)) {
    return 'ios';
  }
  return 'web';
}

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w.charAt(0).toUpperCase())
    .join('');
}

function getGradient(name: string): string {
  const gradients = [
    'linear-gradient(135deg, #f97316, #ea580c)',
    'linear-gradient(135deg, #f97316, #f59e0b)',
    'linear-gradient(135deg, #ea580c, #c2410c)',
    'linear-gradient(135deg, #f97316, #e11d48)',
    'linear-gradient(135deg, #f59e0b, #d97706)',
    'linear-gradient(135deg, #64748b, #475569)',
    'linear-gradient(135deg, #f97316, #475569)',
  ];
  const idx = name.charCodeAt(0) % gradients.length;
  return gradients[idx];
}

// ── App Card ─────────────────────────────────────────────────────────────────

const AppCard: React.FC<{
  app: PortalApp;
  index: number;
  userDevice: SupportedPlatform;
  selectedTab: FilterTab;
}> = ({ app, index, userDevice, selectedTab }) => {
  const { slug } = useParams<{ slug: string }>();

  const supportedPlatforms = app.platforms || ['web'];

  // Check if app can be launched on user's device
  const isAvailable = (function () {
    if (userDevice === 'web') {
      return supportedPlatforms.includes('web');
    }
    if (userDevice === 'ios') {
      return supportedPlatforms.includes('ios') || supportedPlatforms.includes('web');
    }
    if (userDevice === 'android') {
      return supportedPlatforms.includes('android') || supportedPlatforms.includes('web');
    }
    return supportedPlatforms.includes(userDevice);
  })();

  const formatPlatformName = (p: string) => {
    if (p === 'ios') return 'iOS';
    if (p === 'android') return 'Android';
    return 'Web';
  };

  const getPlatformIcon = (p: string) => {
    if (p === 'ios') return Tablet;
    if (p === 'android') return Smartphone;
    return Monitor;
  };

  const getAvailabilityLabel = () => {
    if (isAvailable) return null;
    if (supportedPlatforms.length === 1) {
      return `${formatPlatformName(supportedPlatforms[0])} Only`;
    }
    return `Unavailable on ${formatPlatformName(userDevice)}`;
  };

  const handleClick = async (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    if (!isAvailable || !app.base_url) {
      return;
    }

    const idpToken = localStorage.getItem('idp_token');
    if (!idpToken) {
      toast.error('Session expired. Please log in again.');
      return;
    }

    // 1. If user device is iOS & app platform is iOS -> treat as iOS deep link
    const isIosDeepLink = userDevice === 'ios' && supportedPlatforms.includes('ios');
    // 2. If user device is Android & app platform is Android -> treat as Android deep link
    const isAndroidDeepLink = userDevice === 'android' && supportedPlatforms.includes('android');

    // Deep link if user device matches native app platform (iOS/Android)
    const isDeepLink = isIosDeepLink || isAndroidDeepLink;

    // 3. For web applications, open blank tab synchronously to prevent popup blocking.
    let newWindow: Window | null = null;
    if (!isDeepLink) {
      newWindow = window.open('about:blank', '_blank');
    }

    try {
      const response = await http.post<{ access_token: string; refresh_token?: string }>('/auth/tokens', {
        audience: app.resource_id,
        scope: app.resource_id,
      });

      let targetUrl: string;
      try {
        const url = new URL(app.base_url);
        url.searchParams.append('access_token', idpToken);
        url.searchParams.append('platform_token', response.access_token);
        if (response.refresh_token) {
          url.searchParams.append('refresh_token', response.refresh_token);
        }
        if (slug) {
          url.searchParams.append('tenant', slug);
        }
        targetUrl = url.toString();
      } catch {
        const separator = app.base_url.includes('?') ? '&' : '?';
        let params = `access_token=${encodeURIComponent(idpToken)}&platform_token=${encodeURIComponent(response.access_token)}`;
        if (response.refresh_token) {
          params += `&refresh_token=${encodeURIComponent(response.refresh_token)}`;
        }
        if (slug) {
          params += `&tenant=${encodeURIComponent(slug)}`;
        }
        targetUrl = `${app.base_url}${separator}${params}`;
      }

      if (isDeepLink) {
        // Deep links open in the same page/window
        window.location.href = targetUrl;
      } else {
        // Web applications open in a new window without impacting the current window
        if (newWindow && !newWindow.closed) {
          newWindow.location.href = targetUrl;
        } else {
          const win = window.open(targetUrl, '_blank');
          if (!win) {
            toast.error('Popup blocked. Please allow popups to open this web application.');
          }
        }
      }
    } catch (error: any) {
      if (newWindow && !newWindow.closed) {
        newWindow.close();
      }
      toast.error('Failed to launch application');
      console.error('Failed to fetch platform token', error);
    }
  };

  const availabilityLabel = getAvailabilityLabel();

  return (
    <motion.div
      className={`portal-app-card group relative flex flex-col justify-between p-5 rounded-2xl border transition-all duration-300 ${isAvailable
        ? 'bg-white/80 dark:bg-slate-900/60 backdrop-blur-xl border-slate-200/80 dark:border-slate-800/80 hover:border-orange-500/40 dark:hover:border-orange-500/50 shadow-sm hover:shadow-xl hover:shadow-orange-500/10 hover:-translate-y-1 cursor-pointer'
        : 'bg-slate-100/40 dark:bg-slate-950/30 backdrop-blur-md border-slate-200/40 dark:border-slate-800/30 opacity-60 grayscale-[30%] cursor-not-allowed select-none'
        }`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: index * 0.05, ease: 'easeOut' }}
      onClick={isAvailable ? handleClick : undefined}
      role={isAvailable ? 'button' : 'article'}
      tabIndex={isAvailable ? 0 : -1}
      onKeyDown={(e) => {
        if (isAvailable && (e.key === 'Enter' || e.key === ' ')) handleClick(e);
      }}
    >
      {/* Subtle orange accent glow line at top on hover */}
      {isAvailable && (
        <div className="absolute top-0 left-6 right-6 h-[2px] bg-gradient-to-r from-transparent via-orange-500/0 group-hover:via-orange-500/70 to-transparent transition-all duration-500 rounded-full" />
      )}

      <div>
        {/* Card Header: Logo + Availability Icon/Badge */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="portal-app-logo-wrap relative shrink-0">
            {app.logo_url ? (
              <img
                src={app.logo_url}
                alt={app.name}
                className="w-12 h-12 rounded-xl object-cover ring-1 ring-black/5 dark:ring-white/10 group-hover:scale-105 transition-transform duration-300 shadow-sm"
                onError={(e) => {
                  (e.currentTarget as HTMLImageElement).style.display = 'none';
                  (e.currentTarget.nextSibling as HTMLElement).style.display = 'flex';
                }}
              />
            ) : null}
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center font-bold text-white text-lg shadow-sm group-hover:scale-105 transition-transform duration-300"
              style={{
                background: getGradient(app.name),
                display: app.logo_url ? 'none' : 'flex',
                opacity: isAvailable ? 1 : 0.6,
              }}
            >
              {getInitials(app.name)}
            </div>
          </div>

          {!isAvailable && availabilityLabel ? (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-500/10 text-indigo-600 dark:text-indigo-300 border border-indigo-500/20 shadow-xs">
              <Lock className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
              <span>{availabilityLabel}</span>
            </span>
          ) : (
            <div
              className="p-1.5 rounded-full bg-emerald-500/10 dark:bg-emerald-500/15 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400"
              title="Available on your device"
            >
              <CheckCircle2 className="w-4 h-4" />
            </div>
          )}
        </div>

        {/* App Title & Description */}
        <div className="mb-4">
          <h3 className="font-bold text-base text-slate-900 dark:text-slate-100 group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors line-clamp-1">
            {app.name}
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-2 mt-1 leading-relaxed min-h-[2.25rem]">
            {app.description || 'No description provided.'}
          </p>
        </div>
      </div>

      {/* Card Footer: Platforms + Action Button */}
      <div className="pt-3 border-t border-slate-100 dark:border-slate-800/80 flex items-center justify-between gap-2 mt-auto">
        <div className="flex items-center gap-1 flex-wrap">
          {supportedPlatforms.map((p) => {
            const Icon = getPlatformIcon(p);
            const isHighlighted = selectedTab === 'all' ? p === userDevice : p === selectedTab;
            return (
              <span
                key={p}
                title={formatPlatformName(p)}
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium transition-colors ${isHighlighted && isAvailable
                  ? 'bg-orange-500/15 text-orange-600 dark:text-orange-400 font-semibold border border-orange-500/30'
                  : 'bg-slate-100 dark:bg-slate-800/60 text-slate-400 dark:text-slate-500 border border-transparent'
                  }`}
              >
                <Icon className="w-3 h-3" />
                <span>{formatPlatformName(p)}</span>
              </span>
            );
          })}
        </div>

        {app.base_url && (
          <div className="shrink-0">
            {isAvailable ? (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-orange-600 dark:text-orange-400 group-hover:translate-x-0.5 transition-transform duration-200">
                <span>Launch</span>
                <ExternalLink className="w-3.5 h-3.5" />
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-xs font-medium text-slate-400 dark:text-slate-500">
                <Ban className="w-3.5 h-3.5 text-slate-400 dark:text-slate-500" />
                <span>Unavailable</span>
              </span>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};

// ── Empty State ───────────────────────────────────────────────────────────────

const EmptyApps: React.FC = () => (
  <motion.div
    className="portal-empty"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.4 }}
  >
    <div className="portal-empty-icon">
      <Package className="h-8 w-8" />
    </div>
    <h3 className="portal-empty-title">No applications yet</h3>
    <p className="portal-empty-desc">
      Your administrator hasn't enabled any applications for this tenant yet.
      Check back later.
    </p>
  </motion.div>
);

const EmptyFilterApps: React.FC<{ tabLabel: string }> = ({ tabLabel }) => (
  <motion.div
    className="portal-empty"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    transition={{ duration: 0.4 }}
  >
    <div className="portal-empty-icon">
      <Package className="h-8 w-8" />
    </div>
    <h3 className="portal-empty-title">No {tabLabel} applications</h3>
    <p className="portal-empty-desc">
      There are no applications available for the "{tabLabel}" filter.
    </p>
  </motion.div>
);

// ── Pending Banner ────────────────────────────────────────────────────────────

const PendingBanner: React.FC = () => (
  <motion.div
    className="portal-pending"
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
  >
    <div className="portal-pending-icon">
      <Clock3 className="h-8 w-8" />
    </div>
    <div>
      <h3 className="portal-pending-title">Account pending approval</h3>
      <p className="portal-pending-desc">
        Your account has been created and is waiting for a platform administrator
        to approve your access. You'll receive access once approved.
      </p>
    </div>
  </motion.div>
);

// ── Main Portal Page ──────────────────────────────────────────────────────────

export const PortalPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user, apps, isLoading, error, fetch } = usePortal();
  const [userDevice] = useState<SupportedPlatform>(detectPlatform());
  const [selectedTab, setSelectedTab] = useState<FilterTab>('all');

  const load = useCallback(async () => {
    await fetch();
  }, [fetch]);

  useEffect(() => {
    load();
  }, [load]);

  // Redirect pending users to the waiting room
  useEffect(() => {
    if (error === 'user_pending' && slug) {
      navigate(`/t/${slug}/waiting-room`);
    }
  }, [error, slug, navigate]);

  if (isLoading) {
    return <Loader />;
  }

  if (error && error !== 'user_pending') {
    return (
      <div className="portal-error">
        <AlertCircle className="h-8 w-8 text-red-400" />
        <p className="portal-error-msg">{error}</p>
        <button className="portal-retry" onClick={load}>
          Try again
        </button>
      </div>
    );
  }

  const greeting = user
    ? `Welcome back, ${user.name || user.email}`
    : 'Welcome';

  // Dynamically extract supported platforms across all available apps
  const availablePlatforms = Array.from(
    new Set(
      apps.flatMap((app) => app.platforms || ['web', 'android', 'ios'])
    )
  );

  const allPossibleTabs: { id: FilterTab; label: string; icon: any }[] = [
    { id: 'all', label: 'All', icon: LayoutGrid },
    { id: 'web', label: 'Web', icon: Monitor },
    { id: 'android', label: 'Android', icon: Smartphone },
    { id: 'ios', label: 'iOS', icon: Tablet },
  ];

  // Only show 'All' and platform tabs that exist in at least one tenant application
  const tabOptions = allPossibleTabs.filter(
    (tab) => tab.id === 'all' || availablePlatforms.includes(tab.id as SupportedPlatform)
  );

  const filteredApps = apps.filter((app) => {
    if (selectedTab === 'all') return true;
    const supported = app.platforms || ['web', 'android', 'ios'];
    return supported.includes(selectedTab);
  });

  const currentTabLabel = tabOptions.find((t) => t.id === selectedTab)?.label || 'Selected';

  return (
    <div className="portal-page">
      {/* Hero greeting */}
      <motion.div
        className="portal-hero"
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className="portal-hero-title">{greeting}</h1>
        <p className="portal-hero-sub">
          Select an application to get started
        </p>

        {/* Filter Tab Selector */}
        <div className="mt-5 flex items-center justify-center">
          <div className="inline-flex items-center gap-2 bg-slate-100/90 dark:bg-slate-900/70 p-1.5 rounded-xl border border-slate-200 dark:border-slate-800/80 shadow-sm backdrop-blur-md">
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 pl-2">
              Platform:
            </span>
            <div className="flex items-center gap-1">
              {tabOptions.map((item) => {
                const Icon = item.icon;
                const active = selectedTab === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => setSelectedTab(item.id)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${active
                      ? 'bg-orange-500 text-white shadow-md shadow-orange-500/20'
                      : 'text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-slate-200/50 dark:hover:bg-slate-800/50'
                      }`}
                    title={`Filter by ${item.label}`}
                  >
                    <Icon className="w-3.5 h-3.5" />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Pending state */}
      {user?.status === 0 && <PendingBanner />}

      {/* App grid */}
      {user?.status !== 0 && (
        <>
          {apps.length === 0 ? (
            <EmptyApps />
          ) : filteredApps.length === 0 ? (
            <EmptyFilterApps tabLabel={currentTabLabel} />
          ) : (
            <div className="portal-apps-grid">
              {filteredApps.map((app, i) => (
                <AppCard
                  key={app.id}
                  app={app}
                  index={i}
                  userDevice={userDevice}
                  selectedTab={selectedTab}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PortalPage;
